<app-popup
  [isOpen]="isOpen()"
  size="xxlarge"
  color="mint"
  variant="secondary"
  [title]="'VIBRATION.MULTI_ASSIGNMENT' | translate"
  (closed)="closeDialog()">

  <div class="assign-modal">

    <!-- ── Filtres ────────────────────────────────────────────────────────── -->
    <div class="assign-modal__filters">
      <div class="field">
        <span class="field__label">
          {{ 'VIBRATION.SERIAL_NUMBER' | translate }}<span class="field__required">*</span>
        </span>
        <app-select
          [searchable]="true"
          [multiple]="true"
          [withCheckboxes]="true"
          [clearable]="true"
          [options]="deviceOptions()"
          size="medium"
          [ngModel]="serialNumberFilter()"
          [placeholder]="'FILTERS.SERIAL_NUMBER_PLACEHOLDER' | translate"
          (selectionsChange)="onSerialNumbersSelected($event)">
        </app-select>
      </div>

      <div class="field">
        <div class="field__label">
          {{ 'VIBRATION.START_DATE' | translate }}<span class="field__required">*</span>
        </div>
        <app-calendar
          [ngModel]="startDateFilter()"
          (ngModelChange)="onStartDateChange($event)">
        </app-calendar>
      </div>

      <div class="field">
        <div class="field__label">
          {{ 'VIBRATION.END_DATE' | translate }}<span class="field__required">*</span>
        </div>
        <app-calendar
          [ngModel]="endDateFilter()"
          (ngModelChange)="onEndDateChange($event)">
        </app-calendar>
      </div>

      <div class="field">
        <div class="field__label">
          {{ 'VIBRATION.NOTE' | translate }}<span class="field__required">*</span>
        </div>
        <app-input
          [placeholder]="'VIBRATION.NOTE' | translate"
          [ngModel]="noteFilter()"
          (ngModelChange)="onNoteInput($event)">
        </app-input>
      </div>
    </div>

    <!-- ── Liste des enregistrements ──────────────────────────────────────── -->
    <app-scrollable class="assign-modal__list" (scrolledToEnd)="onScrolledToEnd()">
      <app-data-table class="assign-table"
        [data]="rows()"
        [columns]="tableColumns"
        rowKey="id"
        [equalColumns]="true"
        sortAscIcon="assets/icon/new_icon/arrow_full_down.svg"
        sortDescIcon="assets/icon/new_icon/arrow_full_up.svg"
        sortIconFilter="brightness(0)">

        <!-- En-tête « Channel N » avec navigation entre canaux -->
        <ng-template tableHeader="channel">
          <span class="assign-table__th-channel">
            {{ 'VIBRATION.CHANNEL' | translate }} {{ selectedChannelIndex() + 1 }}
            @if (maxChannelCount() > 1) {
              <app-button variant="tertiary" size="xsmall" [addLabel]="false" [addIconLeft]="true" (clicked)="cycleChannel(-1)">
                <img slot="icon-left" src="assets/icons/arrow-left.svg" alt="" />
              </app-button>
              <app-button variant="tertiary" size="xsmall" [addLabel]="false" [addIconLeft]="true" (clicked)="cycleChannel(1)">
                <img slot="icon-left" src="assets/icons/arrow-right.svg" alt="" />
              </app-button>
            }
          </span>
        </ng-template>

        <ng-template tableCell="select" let-row>
          <app-checkbox class="assign-table__checkbox" size="small" color="mint"
            [ngModel]="isSelected(row.id)"
            (ngModelChange)="toggleSelect(row.id)">
          </app-checkbox>
        </ng-template>

        <ng-template tableCell="channel" let-row>
          @if (previewFor(row, selectedChannelIndex()); as preview) {
            <img class="assign-table__preview" [src]="preview" alt="" />
          } @else {
            <span class="assign-table__empty">-</span>
          }
        </ng-template>

        <ng-template tableCell="fileName" let-row>
          <span class="assign-table__filename">{{ row.fileName }}</span>
        </ng-template>

        <ng-template tableCell="timestampMs" let-row>
          <div class="assign-table__stack">
            <div>{{ row.dateLabel }}</div>
            <div>{{ row.timeLabel }}</div>
          </div>
        </ng-template>

        <ng-template tableCell="notes" let-row>
          <div class="assign-table__stack">
            @if (row.note1) { <div>{{ row.note1 }}</div> }
            @if (row.note2) { <div>{{ row.note2 }}</div> }
            @if (row.note3) { <div>{{ row.note3 }}</div> }
            @if (!row.note1 && !row.note2 && !row.note3) { <div class="assign-table__empty">-</div> }
          </div>
        </ng-template>

        <ng-template tableCell="maxPpv" let-row>
          <div class="assign-table__grid">
            <span>V : {{ row.vAbs != null ? (row.vAbs | number:'1.2-2') : '-' }}</span>
            <span>L : {{ row.lAbs != null ? (row.lAbs | number:'1.2-2') : '-' }}</span>
            <span>T : {{ row.tAbs != null ? (row.tAbs | number:'1.2-2') : '-' }}</span>
            <span>VS : {{ row.vecSomme != null ? (row.vecSomme | number:'1.2-2') : '-' }}</span>
          </div>
        </ng-template>

        <ng-template tableCell="controlPointName" let-row>
          <div class="assign-table__stack">
            <div class="assign-line">
              <img src="assets/icon/device.svg" alt="" />{{ row.deviceLabel || '-' }}
            </div>
            @if (row.controlPointName) {
              <div class="assign-line">
                <img src="assets/icon/control-point-black.svg" alt="" />{{ row.controlPointName }}
              </div>
            } @else {
              <div class="assign-line assign-line--muted">
                <img src="assets/icon/question-mark.svg" alt="" />
                {{ 'VIBRATION.MEASUREMENT_POINT' | translate }} · {{ 'VIBRATION.UNASSIGNED' | translate }}
              </div>
            }
          </div>
        </ng-template>

      </app-data-table>
    </app-scrollable>

    <!-- ── Cible de l'affectation ─────────────────────────────────────────── -->
    <div class="assign-modal__target">
      <div class="field">
        <div class="field__label">
          {{ 'VIBRATION.CONTROL_POINT' | translate }}<span class="field__required"></span>
        </div>
        <app-select
          [options]="controlPointOptions()"
          size="large"
          [ngModel]="selectedControlPointId()"
          [placeholder]="'VIBRATION.SELECT_CONTROL_POINT' | translate"
          (selectionChange)="onControlPointSelected($event)">
        </app-select>
      </div>

      <div class="field">
        <div class="field__label">
          {{ 'VIBRATION.BLAST' | translate }}<span class="field__required">*</span>
        </div>
        <app-input [ngModel]="tirName()" [disabled]="true" size="large"></app-input>
      </div>
    </div>

    @if (errorMessage() !== '') {
      <div class="assign-modal__error">
        <img src="assets/icon/Polygon_danger.svg" alt="" />
        <span>{{ errorMessage() }}</span>
        <app-button variant="tertiary" size="xsmall" [addLabel]="false" [addIconLeft]="true" (clicked)="closeErrorMessage()">
          <img slot="icon-left" src="assets/icon/close.svg" alt="" />
        </app-button>
      </div>
    }

  </div>

  <!-- ── Actions ──────────────────────────────────────────────────────────── -->
  <div slot="footer" class="assign-modal__actions">
    <app-button variant="secondary" size="small" color="mint" [label]="'BUTTONS.CANCEL' | translate" (clicked)="closeDialog()"></app-button>
    <app-button variant="primary" size="small" color="mint" [label]="'BUTTONS.CONFIRM' | translate" (clicked)="confirm()"></app-button>
  </div>

</app-popup>
